"use client"

import clsx from "clsx"
import useEmblaCarousel from "embla-carousel-react"
import Image, { ImageProps } from "next/image"
import React, { useCallback, useEffect, useState } from "react"

import { runIfEnv } from "@/helpers/utils"

import Button, { ButtonProps } from "../Button"
import PageGrid from "../PageGrid"

// Helper function to extract text from React nodes for keys
const getTextFromNode = (node: React.ReactNode): string => {
  if (typeof node === "string") return node
  if (typeof node === "number") return node.toString()
  return ""
}

export type QuoteCardItem = {
  companyMark: ImageProps
  quote: React.ReactNode
  authorName: React.ReactNode
  authorImage?: ImageProps
  authorTitle: React.ReactNode
}

export type QuoteCarouselProps = React.ComponentPropsWithoutRef<"section"> & {
  quoteItems: QuoteCardItem[]
  callToAction?: Omit<ButtonProps, "variant">
  showAuthorImage?: boolean
}

const QuoteCarouselSection: React.FC<QuoteCarouselProps> = (props) => {
  const {
    quoteItems,
    className,
    callToAction,
    showAuthorImage,
    ...restOfHtmlAttrs
  } = props

  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: false,
    align: "center",
    skipSnaps: false,
    dragFree: false,
    duration: 20,
    slidesToScroll: 1,
  })

  const [selectedIndex, setSelectedIndex] = useState(0)

  const scrollTo = useCallback(
    (index: number) => emblaApi && emblaApi.scrollTo(index),
    [emblaApi],
  )

  const onSelect = useCallback(() => {
    if (!emblaApi) return
    setSelectedIndex(emblaApi.selectedScrollSnap())
  }, [emblaApi])

  useEffect(() => {
    if (!emblaApi) return

    onSelect()
    emblaApi.on("reInit", onSelect)
    emblaApi.on("select", onSelect)
  }, [emblaApi, onSelect])

  if (!quoteItems || quoteItems.length === 0) {
    runIfEnv("development", () => {
      console.error("Missing quoteItems for QuoteCarouselSection")
    })
    return null
  }

  /**
   * Validates that all  required fields are present, this will be the array we use to render the quotes and the carousel dots
   */
  const validateRequiredCarouselValues = quoteItems.filter((item) => {
    const { authorName, authorTitle, quote, companyMark } = item

    return [authorName, authorTitle, quote, companyMark].every(Boolean)
  })

  if (validateRequiredCarouselValues.length !== quoteItems.length) {
    runIfEnv("development", () => {
      console.error(
        "Seems like some quoteItems are missing required fields and were not rendered",
      )
    })
  }

  return (
    <section
      className={clsx("py-10 xl:py-28 lg:py-16 md:py-14 bg-gray-0", className)}
      {...restOfHtmlAttrs}
    >
      {/* Embla viewport */}
      <div className="embla" ref={emblaRef}>
        <div className="embla__container flex">
          {validateRequiredCarouselValues?.map((slide, index) => {
            const { authorName, authorTitle, authorImage, quote, companyMark } =
              slide

            return (
              <div
                key={getTextFromNode(authorName) || `slide-${index}`}
                className="embla__slide flex-[0_0_100%] min-w-0"
              >
                <PageGrid>
                  <div className="p-4 mb-8 md:mb-10 lg:mb-0 lg:col-span-4 col-span-full">
                    <Image
                      {...companyMark}
                      className={clsx(
                        "xl:h-[52px] lg:h-[46px] h-[40px] object-contain object-left lg:object-center",
                        companyMark?.className,
                      )}
                    />
                  </div>
                  <div className="p-4 lg:col-span-8 col-span-full">
                    <Image
                      sizes="(min-width: 56px) 56px, 100vw"
                      width={23}
                      height={23}
                      src="/homepage/quote-marks@2x.png"
                      alt="Decorative Quotation Marks"
                      className="h-[23px] w-[23px] mb-8 object-contain object-center"
                    />
                    <p className="mb-8 body1 text-gray-90 xl:mb-12 md:mb-10">
                      {quote}
                    </p>
                    <div className="flex items-center">
                      {authorImage && showAuthorImage && (
                        <Image
                          {...authorImage}
                          width={200}
                          height={200}
                          className={clsx(
                            "xl:h-[36px] lg:h-[28px] h-[24px] object-contain object-left lg:object-center",
                            authorImage?.className,
                          )}
                        />
                      )}

                      <div>
                        <p className="uppercase caption2 text-gray-90">
                          {authorName}
                          <br />
                          {authorTitle}
                        </p>
                      </div>
                    </div>
                  </div>
                </PageGrid>
              </div>
            )
          })}
        </div>
      </div>

      {/* Dot navigation */}
      <PageGrid>
        <div className="p-4 col-span-full lg:col-start-5 lg:col-span-8">
          <div className="flex items-center mb-10 -ml-2">
            {validateRequiredCarouselValues?.map((slide, index) => (
              <button
                key={getTextFromNode(slide.authorName) || `dot-${index}`}
                onClick={() => scrollTo(index)}
                className="p-4 relative z-10"
                type="button"
                aria-label={`Go to slide ${index + 1}`}
              >
                <div
                  className={clsx(
                    "rounded-full mr-8",
                    selectedIndex === index
                      ? "rounded-full bg-blue-50 w-3 h-3"
                      : "w-2 h-2 bg-gray-60",
                  )}
                />
              </button>
            ))}
          </div>
          {callToAction && <Button {...callToAction} variant="tertiary" />}
        </div>
      </PageGrid>
    </section>
  )
}

export default QuoteCarouselSection
